import React, { useEffect, useRef, useState } from "react";
import { CircularProgress, Typography } from "@mui/material";

/* global Autodesk */

const ForgeViewer = ({ url = `${process.env.REACT_APP_API_URL}/StdModel/output/1/result.svf`, loading }) => {
    const viewerRef = useRef(null);
    const containerRef = useRef(null);

    const [viewerReady, setViewerReady] = useState(false);

    const adjustInitialCamera = (viewer, documentUrl) => {
        const isHydrovault = documentUrl.includes("/Hydrovault/");

        // Fit model first
        viewer.fitToView();

        // Only shift Hydrovault
        if (isHydrovault) {
            const navigation = viewer.navigation;

            const position = navigation.getPosition().clone();
            const target = navigation.getTarget().clone();

            const shift = 500;

            // Shift camera left so model appears further right
            position.x -= shift;
            target.x -= shift;

            navigation.setView(position, target);
        }

        // Only show viewer AFTER camera is already positioned
        setViewerReady(true);
    };

    const initViewer = async (documentUrl) => {
        const options = {
            env: "Local",
            document: documentUrl,
        };

        setViewerReady(false);

        if (viewerRef.current) {
            try {
                await viewerRef.current.tearDown();

                viewerRef.current.addEventListener(
                    Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
                    () => {
                        adjustInitialCamera(viewerRef.current, documentUrl);
                    },
                    { once: true },
                );

                await viewerRef.current.start(options.document, options);
            } catch (err) {
                console.error("Error reinitializing viewer:", err);
            }
        } else {
            try {
                await new Promise((resolve, reject) => {
                    Autodesk.Viewing.Initializer(options, () => {
                        try {
                            viewerRef.current = new Autodesk.Viewing.GuiViewer3D(containerRef.current, {
                                extensions: [],
                            });

                            viewerRef.current.addEventListener(
                                Autodesk.Viewing.GEOMETRY_LOADED_EVENT,
                                () => {
                                    adjustInitialCamera(viewerRef.current, documentUrl);
                                },
                                { once: true },
                            );

                            viewerRef.current.start(options.document, options);

                            resolve();
                        } catch (err) {
                            reject(err);
                        }
                    });
                });
            } catch (err) {
                console.error("Error initializing viewer:", err);
            }
        }
    };

    const loadViewer = async (documentUrl) => {
        if (window.Autodesk) {
            await initViewer(documentUrl);
            return;
        }

        try {
            await new Promise((resolve, reject) => {
                const script = document.createElement("script");

                script.src =
                    "https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/viewer3D.min.js";

                script.onload = () => {
                    const style = document.createElement("link");

                    style.rel = "stylesheet";
                    style.href =
                        "https://developer.api.autodesk.com/modelderivative/v2/viewers/7.*/style.min.css";

                    style.onload = () => resolve();

                    style.onerror = () => reject(new Error("Failed to load viewer stylesheet"));

                    document.head.appendChild(style);
                };

                script.onerror = () => reject(new Error("Failed to load viewer script"));

                document.head.appendChild(script);
            });

            await initViewer(documentUrl);
        } catch (err) {
            console.error("Error loading viewer scripts:", err);
        }
    };

    useEffect(() => {
        loadViewer(url);

        return () => {
            if (viewerRef.current) {
                viewerRef.current.tearDown();
                viewerRef.current = null;
            }
        };
    }, [url]);

    return (
        <div
            style={{
                width: "100%",
                height: "100%",
                minHeight: "400px",
                position: "relative",
            }}
        >
            <div
                id="forgeViewer"
                ref={containerRef}
                style={{
                    width: "100%",
                    height: "100%",
                    minHeight: "400px",

                    // Hide viewer until camera is positioned
                    opacity: viewerReady ? 1 : 0,

                    // No transition = no visible camera movement
                    transition: "none",
                }}
            />

            {(loading || !viewerReady) && (
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: "rgba(255, 255, 255, 0.8)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        zIndex: 1000,
                        flexDirection: "column",
                    }}
                >
                    <CircularProgress size={40} />

                    <Typography variant="body2" sx={{ mt: 2 }}>
                        Generating model...
                    </Typography>
                </div>
            )}
        </div>
    );
};

export default ForgeViewer;
